/**
 * Hook de acompanhamento de processamento de propriedade via SSE
 * (gn-api-consolidate-service) com fallback automatico para polling em
 * `GET /api/v1/process/{file_id}` (property-service) caso o EventSource
 * falhe (DP-4a).
 *
 * Uso tipico no toggle "Atualizar dados" de `PropertyTabs.jsx`:
 *
 *   const { status, terminal, error, source } =
 *     usePropertyProcessingStream(fileId, { enabled: isProcessing });
 *
 *   useEffect(() => {
 *     if (!terminal) return;
 *     // hidratar snapshot final via GET /api/v1/state/{fileId}
 *   }, [terminal]);
 *
 * ### Logica de fallback
 *
 * 1. Abre EventSource -> se onopen dispara, `source='sse'`.
 * 2. Se `onerror` dispara sem antes ter aberto (OR >=3 reconexoes em
 *    10s), fecha SSE e inicia polling em `/api/v1/process/{fileId}`.
 * 3. Em modo polling, criterio terminal replica o legado:
 *    `final_status in {process_succeeded, process_failed}` ou
 *    `sse_last.status == cache_hit`.
 * 4. Max 120 polls por padrao (mesmo teto do codigo anterior).
 */

import { useEffect, useRef, useState } from 'react';

import { propertyApi } from '../services/BaseApiService';
import {
  openStatusStream,
  isTerminalPropertyProcessingStatus,
} from '../services/ConsolidateService';

const DEBUG = import.meta.env.VITE_DEBUG === 'true';

// Polling so e usado como fallback do SSE (sse_open_failed ou reconnect
// storm). Default conservador (3s) - issue R-1010/#70 reduziu de 1s pre-
// cutover. Floor de 500ms preserva responsividade em testes.
const DEFAULT_POLL_INTERVAL_MS = Math.max(
  500,
  Number(import.meta.env.VITE_POLLING_INTERVAL_SECONDS || 3) * 1000,
);
const DEFAULT_MAX_POLLS = 120;
const SSE_ERROR_WINDOW_MS = 10_000;
const SSE_ERROR_THRESHOLD = 3;

function extractPollingStatus(data) {
  if (!data || typeof data !== 'object') return null;
  const finalStatus = data.final_status || null;
  const lastStatus = data.sse_last?.status || null;
  if (
    finalStatus === 'process_succeeded' ||
    finalStatus === 'process_failed' ||
    lastStatus === 'cache_hit'
  ) {
    // Normaliza para o vocabulario SSE do consolidate.
    return finalStatus || lastStatus;
  }
  return finalStatus || lastStatus || null;
}

export function usePropertyProcessingStream(fileId, opts = {}) {
  const {
    enabled = true,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxPolls = DEFAULT_MAX_POLLS,
  } = opts;

  const [status, setStatus] = useState(null);
  const [terminal, setTerminal] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null); // 'sse' | 'polling' | null

  const closeSseRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const sseErrorTimestampsRef = useRef([]);
  const sseOpenedRef = useRef(false);
  const terminatedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !fileId) return undefined;

    // reset de estado por file_id
    setStatus(null);
    setTerminal(false);
    setError(null);
    setSource(null);
    pollAttemptsRef.current = 0;
    sseErrorTimestampsRef.current = [];
    sseOpenedRef.current = false;
    terminatedRef.current = false;

    function cleanup() {
      try {
        if (closeSseRef.current) closeSseRef.current();
      } catch (_) {
        /* no-op */
      }
      closeSseRef.current = null;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    function markTerminal(finalStatus) {
      if (terminatedRef.current) return;
      terminatedRef.current = true;
      setStatus((prev) => (finalStatus ? { ...(prev || {}), status: finalStatus } : prev));
      setTerminal(true);
      cleanup();
    }

    function startPolling(reason) {
      if (terminatedRef.current) return;
      if (pollTimerRef.current) return;
      if (DEBUG) console.log('[usePropertyProcessingStream] fallback -> polling', reason);
      setSource('polling');
      pollTimerRef.current = setInterval(async () => {
        pollAttemptsRef.current += 1;
        if (pollAttemptsRef.current > maxPolls) {
          setError(new Error('polling_max_attempts_exceeded'));
          markTerminal(null);
          return;
        }
        try {
          const res = await propertyApi.get(`/api/v1/process/${encodeURIComponent(fileId)}`);
          const effective = extractPollingStatus(res?.data);
          if (effective) {
            setStatus({
              filename: res?.data?.filename ?? null,
              status: effective,
            });
            if (isTerminalPropertyProcessingStatus(effective)) {
              markTerminal(effective);
            }
          }
        } catch (err) {
          if (DEBUG) console.warn('[usePropertyProcessingStream] polling error silenciado', err);
          // erros transitorios de polling nao sao fatais; segue tentando.
        }
      }, pollIntervalMs);
    }

    function startSse() {
      setSource('sse');
      closeSseRef.current = openStatusStream(fileId, {
        onOpen: () => {
          sseOpenedRef.current = true;
          if (DEBUG) console.log('[usePropertyProcessingStream] SSE aberto', fileId);
        },
        onMessage: (payload) => {
          setStatus(payload);
          if (isTerminalPropertyProcessingStatus(payload?.status)) {
            markTerminal(payload.status);
          }
        },
        onError: (ev) => {
          if (terminatedRef.current) return;

          // Se nunca abriu, cair direto para polling.
          if (!sseOpenedRef.current) {
            setError(new Error('sse_open_failed'));
            cleanup();
            startPolling('sse_open_failed');
            return;
          }

          // Se abriu uma vez mas esta reconectando > N vezes em janela,
          // desiste de SSE e vai para polling.
          const now = Date.now();
          sseErrorTimestampsRef.current = sseErrorTimestampsRef.current.filter(
            (ts) => now - ts < SSE_ERROR_WINDOW_MS,
          );
          sseErrorTimestampsRef.current.push(now);
          if (sseErrorTimestampsRef.current.length >= SSE_ERROR_THRESHOLD) {
            setError(new Error('sse_reconnect_storm'));
            cleanup();
            startPolling('sse_reconnect_storm');
          } else if (DEBUG) {
            console.warn('[usePropertyProcessingStream] SSE error', ev);
          }
        },
      });
    }

    startSse();
    return cleanup;
  }, [fileId, enabled, pollIntervalMs, maxPolls]);

  return { status, terminal, error, source };
}

export default usePropertyProcessingStream;
